import React, {useRef, useState, useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import {PhotoCamera, Close} from '@material-ui/icons';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import UserService from '../../services/user.service';
import {BRAND} from '../settings/dashboardStyles';

const OCR_IMAGE_MAX_LONG_EDGE = 2200;
const OCR_IMAGE_MIN_LONG_EDGE = 1400;
const OCR_IMAGE_TARGET_BYTES = 4 * 1024 * 1024;
const OCR_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
const OCR_JPEG_QUALITIES = [0.86, 0.78, 0.7, 0.62];
const OCR_RESIZE_FACTORS = [1, 0.85, 0.7, 0.58];

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: 14,
        border: `1px solid ${BRAND.goldSoft}`,
        boxShadow: '0 24px 64px rgba(44, 71, 103, 0.28)',
        overflow: 'hidden',
        background: BRAND.creamSoft,
        [theme.breakpoints.down('xs')]: {
            borderRadius: 0,
            border: 0,
            margin: 0,
            maxHeight: '100%',
            height: '100%',
            width: '100%',
            maxWidth: '100%',
        },
    },
    header: {
        position: 'relative',
        background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.deepDark} 100%)`,
        color: '#ffffff',
        padding: theme.spacing(2.5, 7, 2.5, 3),
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent 0%, ${BRAND.gold} 50%, transparent 100%)`,
        },
    },
    headerEyebrow: {
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: 1.6,
        textTransform: 'uppercase',
        color: '#e8d9b8',
        marginBottom: 4,
    },
    headerTitle: {
        fontSize: '1.15rem',
        fontWeight: 700,
        letterSpacing: 0.3,
        lineHeight: 1.3,
    },
    closeBtn: {
        position: 'absolute',
        right: theme.spacing(1.25),
        top: theme.spacing(1.25),
        color: '#f0d9a8',
        padding: 8,
        '&:hover': {
            background: 'rgba(176, 141, 87, 0.18)',
        },
    },
    stepIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 3),
        background: '#ffffff',
        borderBottom: `1px solid ${BRAND.goldSoft}`,
    },
    stepDot: {
        width: 26,
        height: 26,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.78rem',
        fontWeight: 700,
        background: BRAND.goldBg,
        color: BRAND.deep,
        border: `1.5px solid ${BRAND.goldSoft}`,
        transition: 'all 200ms ease',
    },
    stepDotActive: {
        background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.deepDark} 100%)`,
        color: '#ffffff',
        border: `1.5px solid ${BRAND.deep}`,
        boxShadow: '0 4px 12px rgba(44, 71, 103, 0.25)',
    },
    stepDotDone: {
        background: BRAND.successBg,
        color: BRAND.success,
        border: `1.5px solid ${BRAND.successBorder}`,
        '& svg': {fontSize: 16},
    },
    stepLabel: {
        fontSize: '0.88rem',
        fontWeight: 600,
        color: BRAND.inkSoft,
    },
    stepLabelActive: {
        color: BRAND.deep,
    },
    stepConnector: {
        flex: '0 0 24px',
        height: 2,
        background: BRAND.goldSoft,
        borderRadius: 1,
    },
    content: {
        padding: theme.spacing(3),
        background: BRAND.creamSoft,
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(2),
        },
    },
    mobileScanningContent: {
        [theme.breakpoints.down('xs')]: {
            position: 'relative',
            flex: 1,
            padding: 0,
            background: '#000000',
            overflow: 'hidden',
        },
    },
    videoFrame: {
        position: 'relative',
        width: '100%',
        maxWidth: 720,
        margin: '0 auto',
        borderRadius: 12,
        overflow: 'hidden',
        background: '#000000',
        border: `1px solid ${BRAND.goldSoft}`,
        boxShadow: '0 6px 22px rgba(44, 71, 103, 0.12)',
    },
    mobileVideoFrame: {
        [theme.breakpoints.down('xs')]: {
            width: '100vw',
            maxWidth: 'none',
            height: '100%',
            minHeight: '100%',
            margin: 0,
            border: 0,
            borderRadius: 0,
            boxShadow: 'none',
        },
    },
    video: {
        width: '100%',
        height: 'auto',
        display: 'block',
        background: '#000000',
    },
    mobileVideo: {
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            height: '100%',
            minHeight: '100%',
            objectFit: 'cover',
        },
    },
    canvas: {
        display: 'none',
    },
    overlay: {
        position: 'absolute',
        top: '8%',
        left: '5%',
        right: '5%',
        bottom: '8%',
        border: `2px dashed rgba(240, 217, 168, 0.85)`,
        borderRadius: 12,
        pointerEvents: 'none',
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.35)',
    },
    mobileOverlay: {
        [theme.breakpoints.down('xs')]: {
            top: '13%',
            left: '4%',
            right: '4%',
            bottom: '18%',
            borderWidth: 3,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.42)',
        },
    },
    cornerMark: {
        position: 'absolute',
        width: 22,
        height: 22,
        borderColor: BRAND.gold,
        borderStyle: 'solid',
        borderWidth: 0,
    },
    cornerTL: {top: -2, left: -2, borderTopWidth: 3, borderLeftWidth: 3, borderTopLeftRadius: 6},
    cornerTR: {top: -2, right: -2, borderTopWidth: 3, borderRightWidth: 3, borderTopRightRadius: 6},
    cornerBL: {bottom: -2, left: -2, borderBottomWidth: 3, borderLeftWidth: 3, borderBottomLeftRadius: 6},
    cornerBR: {bottom: -2, right: -2, borderBottomWidth: 3, borderRightWidth: 3, borderBottomRightRadius: 6},
    focusPulse: {
        position: 'absolute',
        width: 72,
        height: 72,
        marginTop: -36,
        marginLeft: -36,
        border: `3px solid ${BRAND.gold}`,
        borderRadius: '50%',
        opacity: 0,
        pointerEvents: 'none',
        boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.25)',
        animation: '$focusPulse 700ms ease-out',
    },
    '@keyframes focusPulse': {
        '0%': {opacity: 0, transform: 'scale(1.6)'},
        '30%': {opacity: 1, transform: 'scale(1)'},
        '100%': {opacity: 0, transform: 'scale(0.85)'},
    },
    tapToFocus: {
        cursor: 'crosshair',
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(30, 53, 83, 0.92)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
        padding: theme.spacing(3),
        textAlign: 'center',
    },
    loadingTitle: {
        marginTop: theme.spacing(2),
        color: '#ffffff',
        fontWeight: 600,
        fontSize: '1.05rem',
    },
    loadingPct: {
        marginTop: theme.spacing(1),
        color: '#f0d9a8',
        fontWeight: 700,
        letterSpacing: 1,
    },
    hint: {
        marginTop: theme.spacing(2.5),
        padding: theme.spacing(1.5, 2),
        background: '#ffffff',
        border: `1px solid ${BRAND.goldSoft}`,
        borderRadius: 10,
        color: BRAND.ink,
        fontSize: '0.9rem',
        lineHeight: 1.55,
        textAlign: 'center',
    },
    statusBanner: {
        marginTop: theme.spacing(1.5),
        padding: theme.spacing(1.25, 2),
        background: BRAND.successBg,
        border: `1px solid ${BRAND.successBorder}`,
        borderRadius: 10,
        color: BRAND.success,
        fontSize: '0.9rem',
        fontWeight: 600,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
    },
    actions: {
        padding: theme.spacing(2, 3),
        background: '#ffffff',
        borderTop: `1px solid ${BRAND.goldSoft}`,
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        [theme.breakpoints.down('xs')]: {
            '& > *': {
                flex: '1 1 auto',
            },
        },
    },
    mobileScanHeader: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 5,
        padding: theme.spacing(1.5, 6, 1.5, 2),
        color: '#ffffff',
        background: 'linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0) 100%)',
        pointerEvents: 'none',
    },
    mobileScanTitle: {
        fontSize: '0.95rem',
        fontWeight: 700,
        textShadow: '0 1px 6px rgba(0,0,0,0.65)',
    },
    mobileScanHint: {
        marginTop: 4,
        fontSize: '0.76rem',
        color: '#f0d9a8',
        textShadow: '0 1px 6px rgba(0,0,0,0.65)',
    },
    mobileScanClose: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        zIndex: 6,
        color: '#ffffff',
        background: 'rgba(0, 0, 0, 0.38)',
        '&:hover': {
            background: 'rgba(0, 0, 0, 0.55)',
        },
    },
    mobileScanActions: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 5,
        padding: theme.spacing(1.5, 2, 2),
        background: 'linear-gradient(0deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0) 100%)',
        borderTop: 0,
        justifyContent: 'center',
        gap: theme.spacing(1),
        '& > *': {
            flex: '1 1 auto',
        },
    },
    cancelBtn: {
        background: '#ffffff',
        color: BRAND.inkSoft,
        fontWeight: 600,
        textTransform: 'none',
        padding: theme.spacing(0.9, 2),
        border: `1.5px solid rgba(90, 102, 117, 0.25)`,
        borderRadius: 8,
        '&:hover': {
            background: 'rgba(90, 102, 117, 0.06)',
            borderColor: BRAND.inkSoft,
        },
    },
    focusBtn: {
        background: '#ffffff',
        color: BRAND.deep,
        fontWeight: 600,
        textTransform: 'none',
        padding: theme.spacing(0.9, 2),
        border: `1.5px solid ${BRAND.goldSoft}`,
        borderRadius: 8,
        '&:hover': {
            background: BRAND.goldBg,
            borderColor: BRAND.gold,
        },
        '&.Mui-disabled': {
            opacity: 0.55,
        },
    },
    captureBtn: {
        background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.deepDark} 100%)`,
        color: '#ffffff',
        fontWeight: 600,
        textTransform: 'none',
        padding: theme.spacing(0.9, 2.25),
        borderRadius: 8,
        boxShadow: '0 6px 18px rgba(44, 71, 103, 0.25)',
        '&:hover': {
            background: `linear-gradient(135deg, ${BRAND.deepDark} 0%, ${BRAND.deep} 100%)`,
            boxShadow: '0 10px 26px rgba(44, 71, 103, 0.35)',
        },
        '&.Mui-disabled': {
            opacity: 0.55,
            color: '#ffffff',
        },
    },
    debugPaper: {
        borderRadius: 12,
        border: `1px solid ${BRAND.goldSoft}`,
    },
    debugHeader: {
        background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.deepDark} 100%)`,
        color: '#ffffff',
        padding: theme.spacing(2, 3),
        fontWeight: 700,
        position: 'relative',
    },
    debugText: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily: 'monospace',
        fontSize: '0.82rem',
        color: BRAND.ink,
        background: BRAND.creamSoft,
        padding: theme.spacing(2),
        borderRadius: 8,
        border: `1px solid ${BRAND.goldSoft}`,
    },
}));

const IdCardScanner = ({open, onClose, onScanComplete, scanType = 'contracting_authority'}) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const canvasRef = useRef(null);
    const nativePhotoInputRef = useRef(null);

    const [isScanning, setIsScanning] = useState(false);
    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('');
    const [scanSide, setScanSide] = useState('front');
    const [frontData, setFrontData] = useState(null);
    const [debugText, setDebugText] = useState('');
    const [showDebug, setShowDebug] = useState(false);

    useEffect(() => {
        if (open) {
            setScanSide('front');
            setFrontData(null);
            setIsScanning(false);
            setProgress(0);
            setStatusText('');
        }
    }, [open]);

    const enhanceImageForOcr = (sourceCanvas, cropToOverlay = true) => {
        const crop = cropToOverlay
            ? {
                x: Math.round(sourceCanvas.width * 0.05),
                y: Math.round(sourceCanvas.height * 0.08),
                width: Math.round(sourceCanvas.width * 0.9),
                height: Math.round(sourceCanvas.height * 0.84)
            }
            : {
                x: 0,
                y: 0,
                width: sourceCanvas.width,
                height: sourceCanvas.height
            };

        const outputCanvas = document.createElement('canvas');
        const longEdge = Math.max(crop.width, crop.height);
        let scale = 1;

        if (longEdge > OCR_IMAGE_MAX_LONG_EDGE) {
            scale = OCR_IMAGE_MAX_LONG_EDGE / longEdge;
        } else if (longEdge < OCR_IMAGE_MIN_LONG_EDGE) {
            scale = Math.min(1.6, OCR_IMAGE_MIN_LONG_EDGE / longEdge);
        }

        outputCanvas.width = Math.round(crop.width * scale);
        outputCanvas.height = Math.round(crop.height * scale);

        const outputContext = outputCanvas.getContext('2d');
        outputContext.imageSmoothingEnabled = true;
        outputContext.imageSmoothingQuality = 'high';
        outputContext.filter = 'contrast(1.18) brightness(1.04) saturate(0.9)';
        outputContext.drawImage(
            sourceCanvas,
            crop.x,
            crop.y,
            crop.width,
            crop.height,
            0,
            0,
            outputCanvas.width,
            outputCanvas.height
        );
        outputContext.filter = 'none';

        const imageData = outputContext.getImageData(0, 0, outputCanvas.width, outputCanvas.height);
        const data = imageData.data;
        const original = new Uint8ClampedArray(data);
        const width = outputCanvas.width;
        const height = outputCanvas.height;
        const amount = 0.65;

        for (let y = 1; y < height - 1; y++) {
            for (let x = 1; x < width - 1; x++) {
                const i = (y * width + x) * 4;
                for (let c = 0; c < 3; c++) {
                    const blurred = (
                        original[i - width * 4 + c] +
                        original[i - 4 + c] +
                        original[i + c] * 4 +
                        original[i + 4 + c] +
                        original[i + width * 4 + c]
                    ) / 8;
                    data[i + c] = Math.max(0, Math.min(255, original[i + c] + amount * (original[i + c] - blurred)));
                }
            }
        }

        outputContext.putImageData(imageData, 0, 0);
        return outputCanvas;
    };

    const resizeCanvas = (sourceCanvas, scale) => {
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = Math.max(1, Math.round(sourceCanvas.width * scale));
        outputCanvas.height = Math.max(1, Math.round(sourceCanvas.height * scale));

        const outputContext = outputCanvas.getContext('2d');
        outputContext.imageSmoothingEnabled = true;
        outputContext.imageSmoothingQuality = 'high';
        outputContext.drawImage(sourceCanvas, 0, 0, outputCanvas.width, outputCanvas.height);

        return outputCanvas;
    };

    const canvasToJpegBlob = (canvas, quality = 0.86) => {
        return new Promise((resolve) => {
            canvas.toBlob(resolve, 'image/jpeg', quality);
        });
    };

    const canvasToUploadBlob = async (canvas) => {
        let lastBlob = null;

        for (const resizeFactor of OCR_RESIZE_FACTORS) {
            const uploadCanvas = resizeFactor === 1 ? canvas : resizeCanvas(canvas, resizeFactor);

            for (const quality of OCR_JPEG_QUALITIES) {
                const blob = await canvasToJpegBlob(uploadCanvas, quality);
                if (!blob) continue;

                lastBlob = blob;
                if (blob.size <= OCR_IMAGE_TARGET_BYTES) {
                    return blob;
                }
            }
        }

        return lastBlob;
    };

    const enhanceBlobForOcr = async (blob, cropToOverlay = true) => {
        if (!window.createImageBitmap) return blob;

        const bitmap = await window.createImageBitmap(blob);
        const canvas = canvasRef.current || document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;

        const context = canvas.getContext('2d');
        context.drawImage(bitmap, 0, 0);
        if (bitmap.close) bitmap.close();

        return canvasToUploadBlob(enhanceImageForOcr(canvas, cropToOverlay));
    };

    const formatFileSize = (bytes) => {
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const openNativeCamera = () => {
        if (nativePhotoInputRef.current) {
            nativePhotoInputRef.current.click();
        }
    };

    const handleNativePhotoSelected = async (event) => {
        const file = event.target.files && event.target.files[0];
        event.target.value = '';
        if (!file || isScanning) return;

        try {
            const enhancedBlob = await enhanceBlobForOcr(file, false);
            processImage(enhancedBlob || file);
        } catch (error) {
            console.warn('Native photo enhancement failed, sending original photo:', error);
            processImage(file);
        }
    };

    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const processImage = async (imageBlob) => {
        setIsScanning(true);
        setProgress(0);
        setStatusText('Odosielanie obrázku do Google Vision OCR...');

        try {
            if (imageBlob.size > OCR_IMAGE_MAX_BYTES) {
                const error = new Error(`Fotka je príliš veľká (${formatFileSize(imageBlob.size)}). Skúste ju odfotiť z väčšej vzdialenosti alebo vybrať menší obrázok.`);
                error.userMessage = error.message;
                throw error;
            }

            const imageBase64 = await blobToBase64(imageBlob);
            setProgress(35);
            setStatusText('Rozpoznávanie textu cez Google Vision OCR...');

            const response = await UserService.scanIdCard({
                imageBase64,
                side: scanSide,
                scanType
            });

            const text = response.data && response.data.text ? response.data.text : '';
            setProgress(100);
            setDebugText(text || 'Google Vision OCR nevrátil žiadny text.');
            setShowDebug(true);

            const extractedData = scanSide === 'front'
                ? extractIdCardDataFront(text)
                : extractIdCardDataBack(text);

            if (scanSide === 'front') {
                setFrontData(extractedData);
                setIsScanning(false);
                setScanSide('back');
                setStatusText('Predná strana naskenovaná. Otočte preukaz na zadnú stranu.');
            } else {
                const completeData = {...frontData, ...extractedData};
                setStatusText('Údaje úspešne naskenované!');
                setTimeout(() => {
                    onScanComplete(completeData);
                    handleClose();
                }, 1000);
            }
        } catch (error) {
            console.error('Error processing image:', error);
            const message = error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : error.userMessage
                    ? error.userMessage
                : 'Chyba pri spracovaní obrázku. Skúste znova.';
            setStatusText(message);
            setTimeout(() => {
                setIsScanning(false);
            }, 2000);
        }
    };

    const getFieldName = (contractingAuthorityField, deceasedField) => {
        return scanType === 'deceased' ? deceasedField : contractingAuthorityField;
    };

    const normalizeOcrText = (value) => {
        return value.replace(/\s+/g, ' ').trim();
    };

    const normalizeLines = (value) => {
        return value.split('\n').map(line => normalizeOcrText(line)).filter(line => line);
    };

    const cleanExtractedValue = (value) => {
        return normalizeOcrText(value || '')
            .replace(/^[/:\-\s]+/, '')
            .replace(/[/:\-\s]+$/, '');
    };

    const cleanNameValue = (value) => {
        return cleanExtractedValue(value)
            .replace(/\b[A-Z]{1,3}\d{6,9}\b/g, '')
            .replace(/\b(Surname|Given names?|Nationality|Sex|No\.?|Issued by|Signature|Date|Rodné|Cislo|Číslo|Štátne|Statne|Pohlavie)\b.*$/i, '')
            .replace(/\s{2,}/g, ' ')
            .trim();
    };

    const findByRegexes = (value, regexes) => {
        for (const regex of regexes) {
            const match = value.match(regex);
            if (match && match[1]) {
                return cleanExtractedValue(match[1]);
            }
        }
        return '';
    };

    const formatBirthNumber = (value) => {
        const match = value && value.match(/(\d{6})\s*\/?\s*(\d{3,4})/);
        return match ? `${match[1]}/${match[2]}` : '';
    };

    const parseOcrDate = (value) => {
        const match = value && value.match(/(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})/);
        if (!match) return null;

        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        const year = parseInt(match[3], 10);
        return new Date(year, month, day);
    };

    const getBirthDateFromBirthNumber = (birthNumber) => {
        const digits = (birthNumber || '').replace(/\D/g, '');
        if (!/^\d{9,10}$/.test(digits)) return null;

        const yearPart = parseInt(digits.slice(0, 2), 10);
        const encodedMonth = parseInt(digits.slice(2, 4), 10);
        const day = parseInt(digits.slice(4, 6), 10);
        let month = encodedMonth;

        if (encodedMonth >= 71 && encodedMonth <= 82) {
            month = encodedMonth - 70;
        } else if (encodedMonth >= 51 && encodedMonth <= 62) {
            month = encodedMonth - 50;
        } else if (encodedMonth >= 21 && encodedMonth <= 32) {
            month = encodedMonth - 20;
        }

        if (month < 1 || month > 12 || day < 1 || day > 31) return null;

        const year = digits.length === 9 || yearPart >= 54 ? 1900 + yearPart : 2000 + yearPart;
        const birthDate = new Date(year, month - 1, day);

        if (
            birthDate.getFullYear() !== year ||
            birthDate.getMonth() !== month - 1 ||
            birthDate.getDate() !== day
        ) {
            return null;
        }

        return birthDate;
    };

    const isLikelyAddressLine = (line) => {
        const lower = line.toLowerCase();
        if (lower.includes('trvalý pobyt') || lower.includes('trvaly pobyt') || lower === 'address') return false;
        if (lower.includes('rodné priezvisko') || lower.includes('surname at birth')) return false;
        if (lower.includes('miesto narodenia') || lower.includes('place of birth')) return false;
        if (lower.includes('osobitné') || lower.includes('osobistné') || lower.includes('special remarks')) return false;
        if (/^[A-Z0-9<]{20,}$/.test(line)) return false;
        return /^[A-ZÁČĎÉÍŇÓŠŤÚÝŽ][A-Za-zÁČĎÉÍŇÓŠŤÚÝŽáčďéíňóšťúýž\s.,/-]{2,60}$/.test(line);
    };

    const extractIdCardDataFront = (text) => {
        const data = {};
        const normalizedText = normalizeOcrText(text);
        const birthNumberField = getFieldName('ca_id', 'd_id');
        const birthDateField = getFieldName('ca_birth', 'd_birth');
        const nameField = getFieldName('contracting_authority', 'deceased');
        const nationalityField = getFieldName('ca_nationality', 'd_nationality');
        const stateField = getFieldName('ca_state', 'd_state');
        const genderField = getFieldName('', 'd_gender');

        const surname = cleanNameValue(findByRegexes(normalizedText, [
            /Priezvisko\s*\/\s*Surname\s+(.+?)\s+Meno\s*\/?\s*Given\s*names?/i,
            /Surname\s+(.+?)\s+Meno\s*\/?\s*Given\s*names?/i,
            /Priezvisko\s+(.+?)\s+Meno/i
        ]));

        const givenName = cleanNameValue(findByRegexes(normalizedText, [
            /Meno\s*\/?\s*Given\s*names?\s+(.+?)\s+(?:[A-Z]{1,3}\d{6,9}|Št[aá]tne|Statne|St[aá]tne|Pohlavie|Poblave|Sex|Číslo|Cislo|No\.)/i,
            /Given\s*names?\s+(.+?)\s+(?:[A-Z]{1,3}\d{6,9}|Št[aá]tne|Statne|St[aá]tne|Pohlavie|Poblave|Sex|Číslo|Cislo|No\.)/i,
            /Meno\s+(.+?)\s+(?:[A-Z]{1,3}\d{6,9}|Št[aá]tne|Statne|St[aá]tne|Pohlavie|Poblave|Sex|Číslo|Cislo|No\.)/i
        ]));

        if (givenName || surname) {
            data[nameField] = [givenName, surname].filter(Boolean).join(' ');
        }

        const nationality = findByRegexes(normalizedText, [
            /(?:Nationality|ob[cč]ianstvo|oblinstvo)\s*\/?\s*(SVK|[A-Z]{3})\b/i,
            /(?:Št[aá]tne|Statne|St[aá]tne).*?\b(SVK|[A-Z]{3})\b/i
        ]);
        if (nationality) {
            data[nationalityField] = nationality.toUpperCase();
        }
        if (/Slovensk[aá]\s+republika|Slovak\s+Republic|\bSVK\b/i.test(normalizedText)) {
            data[stateField] = 'Slovenská republika';
        }

        const gender = findByRegexes(normalizedText, [
            /(?:Pohlavie|Poblave|Pohlave|Sex)\s*\/?\s*(?:Sex\s*)?([MF])\b/i,
            /(?:Pohlavie|Poblave|Pohlave)\s+([MF])\b/i
        ]);
        if (scanType === 'deceased' && genderField && gender) {
            data[genderField] = gender.toUpperCase() === 'M' ? '1 - muž' : '2 - žena';
        }

        const birthDate = findByRegexes(normalizedText, [
            /D[aá]tum\s+narodenia\s*\/?\s*Date\s+of\s+birth\s+(\d{1,2}[.\-/]\d{1,2}[.\-/]\d{4})/i,
            /D[aá]tum\s+narodenia.*?Date\s+of\s+bir\w*\s+(\d{1,2}[.\-/]\d{1,2}[.\-/]\d{4})/i,
            /Date\s+of\s+bir\w*\s+(\d{1,2}[.\-/]\d{1,2}[.\-/]\d{4})/i,
            /D[aá]tum\s+narodenia\s+(\d{1,2}[.\-/]\d{1,2}[.\-/]\d{4})/i
        ]);
        const parsedBirthDate = parseOcrDate(birthDate);
        if (parsedBirthDate) {
            data[birthDateField] = parsedBirthDate;
        }

        const birthNumber = formatBirthNumber(findByRegexes(normalizedText, [
            /Rodn[eé]\s*[čc]íslo\s*\/\s*Personal\s+No\.?\s+(\d{6}\s*\/?\s*\d{3,4})/i,
            /Personal\s+No\.?\s+(\d{6}\s*\/?\s*\d{3,4})/i,
            /\b(\d{6}\s*\/\s*\d{3,4})\b/,
            /\b(\d{10})\b/
        ]));
        if (birthNumber) {
            data[birthNumberField] = birthNumber;
            if (!data[birthDateField]) {
                data[birthDateField] = getBirthDateFromBirthNumber(birthNumber);
            }
        }

        return data;
    };

    const extractIdCardDataBack = (text) => {
        const data = {};
        const lines = normalizeLines(text);
        const cityField = getFieldName('ca_city', 'd_city');
        const addressField = getFieldName('ca_address', 'd_address');

        const addressIndex = lines.findIndex(line => {
            const lower = line.toLowerCase();
            return lower.includes('trvalý pobyt') || lower.includes('trvaly pobyt') || lower === 'address';
        });

        if (addressIndex !== -1) {
            const addressLines = [];
            for (let i = addressIndex + 1; i < lines.length; i++) {
                if (isLikelyAddressLine(lines[i])) {
                    addressLines.push(lines[i]);
                }
                if (addressLines.length === 2) break;
            }

            if (addressLines[0]) {
                data[cityField] = addressLines[0];
            }
            if (addressLines[1]) {
                data[addressField] = addressLines[1];
            }
        }

        if (!data[cityField] && !data[addressField]) {
            const inlineAddress = findByRegexes(normalizeOcrText(text), [
                /(?:Trval[ýy]\s+pobyt\s*)?(?:Address)\s+(.+?)\s+(?:Rodn[eé]\s+priezvisko|Surname\s+at\s+birth|Miesto\s+narodenia|Place\s+of\s+birth|Osob)/i
            ]);
            if (inlineAddress) {
                const inlineAddressLines = inlineAddress.split(/\s{2,}|,/).map(line => cleanExtractedValue(line)).filter(isLikelyAddressLine);
                if (inlineAddressLines[0]) data[cityField] = inlineAddressLines[0];
                if (inlineAddressLines[1]) data[addressField] = inlineAddressLines[1];
            }
        }

        if (scanType === 'deceased') {
            const birthPlacePattern = /(?:miesto\s+narodenia|narodení|born|birth.*place)[:\s]+([A-ZÁČĎÉÍŇÓŠŤÚÝŽ][a-záčďéíňóšťúýž\s,-]+)/i;
            const birthPlaceMatch = text.match(birthPlacePattern);
            if (birthPlaceMatch) {
                data['d_place_birth'] = birthPlaceMatch[1].trim();
            } else {
                for (let i = 0; i < lines.length; i++) {
                    if (lines[i].toLowerCase().includes('narodeni') || lines[i].toLowerCase().includes('miesto')) {
                        if (i + 1 < lines.length) {
                            const nextLine = lines[i + 1];
                            if (nextLine.length > 2 && nextLine.length < 50 && /^[A-ZÁČĎÉÍŇÓŠŤÚÝŽ]/.test(nextLine)) {
                                data['d_place_birth'] = nextLine;
                                break;
                            }
                        }
                    }
                }
            }
        }

        if (scanType === 'deceased') {
            const normalizedText = normalizeOcrText(text);
            const maidenName = cleanNameValue(findByRegexes(normalizedText, [
                /Rodn[eé]\s+priezvisko\s+(.+?)\s+Surname\s+at\s+birth/i,
                /Rodn[eé]\s+priezvisko\s+(.+?)\s+(?:Miesto\s+narodenia|Place\s+of\s+birth|Osob)/i,
                /Surname\s+at\s+birth\s+(.+?)\s+(?:Miesto\s+narodenia|Place\s+of\s+birth|Osob)/i
            ]));

            if (maidenName) {
                data['d_maiden_name'] = maidenName;
            }
        }

        return data;
    };

    const handleClose = () => {
        setIsScanning(false);
        setProgress(0);
        setStatusText('');
        setScanSide('front');
        setFrontData(null);
        onClose();
    };

    const stepFrontActive = scanSide === 'front';
    const stepFrontDone = scanSide === 'back';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            classes={{paper: classes.paper}}
        >
            <div className={classes.header}>
                <Typography className={classes.headerEyebrow}>
                    Skenovanie občianskeho preukazu
                </Typography>
                <Typography className={classes.headerTitle}>
                    {scanSide === 'front'
                        ? 'Odfoťte prednú stranu preukazu'
                        : 'Odfoťte zadnú stranu preukazu'}
                </Typography>
                <IconButton onClick={handleClose} className={classes.closeBtn} aria-label="Zavrieť">
                    <Close/>
                </IconButton>
            </div>

            <div className={classes.stepIndicator}>
                <span className={`${classes.stepDot} ${stepFrontActive ? classes.stepDotActive : ''} ${stepFrontDone ? classes.stepDotDone : ''}`}>
                    {stepFrontDone ? <CheckCircleOutlineIcon/> : '1'}
                </span>
                <span className={`${classes.stepLabel} ${stepFrontActive || stepFrontDone ? classes.stepLabelActive : ''}`}>
                    Predná strana
                </span>
                <span className={classes.stepConnector}/>
                <span className={`${classes.stepDot} ${scanSide === 'back' ? classes.stepDotActive : ''}`}>2</span>
                <span className={`${classes.stepLabel} ${scanSide === 'back' ? classes.stepLabelActive : ''}`}>
                    Zadná strana
                </span>
            </div>

            <DialogContent className={classes.content}>
                <canvas ref={canvasRef} className={classes.canvas}/>
                <input
                    ref={nativePhotoInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{display: 'none'}}
                    onChange={handleNativePhotoSelected}
                />

                <Typography className={classes.hint}>
                    {scanSide === 'front'
                        ? 'Ťuknite na tlačidlo nižšie. Otvorí sa fotoaparát telefónu — použite jeho natívne zaostrenie a odfoťte PREDNÚ stranu občianskeho preukazu.'
                        : 'Ťuknite na tlačidlo nižšie. Otvorí sa fotoaparát telefónu — použite jeho natívne zaostrenie a odfoťte ZADNÚ stranu občianskeho preukazu.'}
                </Typography>

                {isScanning && (
                    <div className={classes.loadingOverlay} style={{position: 'relative', minHeight: 180, borderRadius: 12}}>
                        <CircularProgress size={56} style={{color: '#f0d9a8'}}/>
                        <Typography className={classes.loadingTitle}>
                            {statusText}
                        </Typography>
                        {progress > 0 && (
                            <Typography className={classes.loadingPct}>
                                {progress}%
                            </Typography>
                        )}
                    </div>
                )}

                {statusText && !isScanning && (
                    <div className={classes.statusBanner}>
                        <CheckCircleOutlineIcon fontSize="small"/>
                        {statusText}
                    </div>
                )}
            </DialogContent>

            <DialogActions className={classes.actions}>
                <Button onClick={handleClose} className={classes.cancelBtn}>
                    Zrušiť
                </Button>
                {!isScanning && (
                    <Button
                        onClick={openNativeCamera}
                        className={classes.captureBtn}
                        startIcon={<PhotoCamera/>}
                    >
                        Odfotiť mobilným fotoaparátom
                    </Button>
                )}
            </DialogActions>

            <Dialog
                open={showDebug}
                onClose={() => setShowDebug(false)}
                maxWidth="sm"
                fullWidth
                classes={{paper: classes.debugPaper}}
            >
                <div className={classes.debugHeader}>
                    Naskenovaný text (Debug)
                    <IconButton
                        onClick={() => setShowDebug(false)}
                        style={{position: 'absolute', right: 8, top: 8, color: '#f0d9a8'}}
                        aria-label="Zavrieť"
                    >
                        <Close/>
                    </IconButton>
                </div>
                <DialogContent style={{padding: 24, background: BRAND.creamSoft}}>
                    <div className={classes.debugText}>{debugText}</div>
                </DialogContent>
                <DialogActions style={{padding: 16, background: '#ffffff', borderTop: `1px solid ${BRAND.goldSoft}`}}>
                    <Button onClick={() => setShowDebug(false)} className={classes.captureBtn}>
                        Zatvoriť
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default IdCardScanner;
